import { AuthClient } from "@ncino/pa-authorization-client-webclient";
import { queryOptions, useQuery, useQueryClient, type QueryClient } from "@tanstack/react-query";
import { v4 as uuid } from "uuid";
import { useAuth } from "@/services/auth/auth";
import { useRouteContext } from "@tanstack/react-router";
import { PaxContext } from "@/routes/__root";
import { MarketplaceAccessDeniedError } from "@/shared/errors";

const PERMISSION_STALE_TIME = 10 * 60 * 1000;

type PermissionGroup =
  | "approveRemittance"
  | "publishListing"
  | "importSavedGroupAsPool"
  | "reconcileData"
  | "approveInterest"
  | "initiateInterest"
  | "viewAccessAdmin"
  | "fullAccessAdmin";

type LegacyRight = "nCinoEmployee" | "paRightIdSilverbackAdmin" | "paRightIdSilverbackSuperAdmin";

interface PermissionRequest {
  principalId: string;
  entityType: string;
  entityId: string;
  /**
   * Defaults to `PAX::Action`
   */
  actionType?: string;
  actionId: string;
}

const buildPermissionRequest = ({
  principalId,
  entityType,
  entityId,
  actionType = "PAX::Action",
  actionId,
}: PermissionRequest) => {
  return {
    principalType: "PAX::User",
    principalId,
    entityType,
    entityId,
    actionType,
    actionId,
  };
};

// Mirrors AuthClient.buildKey — the format checkPermissions() keys its result Map by.
const buildPermissionKey = (request: ReturnType<typeof buildPermissionRequest>): string =>
  `${request.principalType}::${request.principalId}::${request.entityType}::${request.entityId}::${request.actionType}::${request.actionId}`;

/* -------------------------------------------------------------------------- */
/* Single permission                                                          */
/* -------------------------------------------------------------------------- */

async function fetchHasPermission(
  permission: PermissionRequest,
  authClient: AuthClient
): Promise<boolean> {
  const perm = await authClient.checkPermission(buildPermissionRequest(permission), {
    correlationId: uuid(),
  });

  return perm !== "DENY";
}

export function hasPermissionQueryOption(permission: PermissionRequest, authClient: AuthClient) {
  return queryOptions({
    queryKey: ["hasPermission", permission] as const,
    queryFn: () => fetchHasPermission(permission, authClient),
    staleTime: PERMISSION_STALE_TIME,
  });
}

export function hasPermission(
  permission: PermissionRequest,
  routeContext: PaxContext
): Promise<boolean> {
  const { queryClient, auth } = routeContext;
  return queryClient.ensureQueryData(hasPermissionQueryOption(permission, auth.authClient));
}

export function useHasPermissionQuery(permission: PermissionRequest) {
  const { authClient, principalId } = useAuth();
  const { institutionId } = useRouteContext({ from: "__root__" });

  return useQuery({
    ...hasPermissionQueryOption(permission, authClient),
    enabled: !!principalId && institutionId != null,
  });
}

/* -------------------------------------------------------------------------- */
/* Batch permissions — one request, each result seeded into its own cache     */
/* -------------------------------------------------------------------------- */

async function fetchHasPermissions(
  permissions: PermissionRequest[],
  authClient: AuthClient,
  queryClient: QueryClient
): Promise<boolean[]> {
  const requests = permissions.map(buildPermissionRequest);

  const res = await authClient.checkPermissions(requests, {
    correlationId: uuid(),
  });

  return permissions.map((permission, i) => {
    const allowed = res.get(buildPermissionKey(requests[i])) === "ALLOW";
    queryClient.setQueryData(hasPermissionQueryOption(permission, authClient).queryKey, allowed);
    return allowed;
  });
}

export function hasPermissionsQueryOption(
  permissions: PermissionRequest[],
  authClient: AuthClient,
  queryClient: QueryClient
) {
  return queryOptions({
    queryKey: ["hasPermissions", permissions] as const,
    queryFn: () => fetchHasPermissions(permissions, authClient, queryClient),
    staleTime: PERMISSION_STALE_TIME,
  });
}

export function hasPermissions(
  permissions: PermissionRequest[],
  routeContext: PaxContext
): Promise<boolean[]> {
  const { queryClient, auth } = routeContext;
  return queryClient.ensureQueryData(
    hasPermissionsQueryOption(permissions, auth.authClient, queryClient)
  );
}

export function useHasPermissionsQuery(permissions: PermissionRequest[]) {
  const { authClient, principalId } = useAuth();
  const { institutionId } = useRouteContext({ from: "__root__" });
  const queryClient = useQueryClient();

  return useQuery({
    ...hasPermissionsQueryOption(permissions, authClient, queryClient),
    enabled: !!principalId && institutionId != null,
  });
}

/* -------------------------------------------------------------------------- */
/* Permission-group membership — convenience wrappers over the generics       */
/* -------------------------------------------------------------------------- */

type PermissionGroupMemberships = Record<PermissionGroup, boolean>;

function buildPermissionGroupRequest(
  permissionGroup: PermissionGroup,
  principalId: string,
  institutionId: number
): PermissionRequest {
  return {
    principalId,
    entityType: "PAX::MKT_INSTITUTION_GROUP",
    entityId: `mkt-inst:${institutionId}::${permissionGroup}`,
    actionId: "isInstitutionMember",
  };
}

export function buildLegacyRightRequest(
  legacyRight: LegacyRight,
  principalId: string
): PermissionRequest {
  return {
    principalId,
    entityType: "PAX::LegacyRightsGroup",
    entityId: `PA_RIGHT::${legacyRight}::portfolio::*`,
    actionId: "hasRight",
  };
}

export function isPermissionGroupMember(
  permissionGroup: PermissionGroup,
  routeContext: PaxContext
): Promise<boolean> {
  const { auth, institutionId } = routeContext;
  return hasPermission(
    buildPermissionGroupRequest(permissionGroup, auth.principalId, institutionId),
    routeContext
  );
}

export function useIsPermissionGroupMemberQuery(permissionGroup: PermissionGroup) {
  const { principalId } = useAuth();
  const { institutionId } = useRouteContext({ from: "__root__" });

  return useHasPermissionQuery(
    buildPermissionGroupRequest(permissionGroup, principalId, institutionId!)
  );
}

export async function batchIsPermissionGroupMember(
  permissionGroups: PermissionGroup[],
  routeContext: PaxContext
): Promise<PermissionGroupMemberships> {
  const { auth, institutionId } = routeContext;
  const groups = [...permissionGroups].sort();
  const requests = groups.map((group) =>
    buildPermissionGroupRequest(group, auth.principalId, institutionId)
  );

  const results = await hasPermissions(requests, routeContext);

  return groups.reduce((acc, group, i) => {
    acc[group] = results[i];
    return acc;
  }, {} as PermissionGroupMemberships);
}

export function useBatchIsPermissionGroupMemberQuery(permissionGroups: PermissionGroup[]) {
  const { principalId } = useAuth();
  const { institutionId } = useRouteContext({ from: "__root__" });

  const groups = [...permissionGroups].sort();
  const requests = groups.map((group) =>
    buildPermissionGroupRequest(group, principalId, institutionId!)
  );

  const query = useHasPermissionsQuery(requests);

  const memberships = groups.reduce((acc, group, i) => {
    acc[group] = query.data?.[i] ?? false;
    return acc;
  }, {} as PermissionGroupMemberships);

  return { ...query, data: memberships };
}

function buildCanViewSettingsRequests(
  principalId: string,
  institutionId: number
): PermissionRequest[] {
  return [
    buildPermissionGroupRequest("viewAccessAdmin", principalId, institutionId),
    buildLegacyRightRequest("paRightIdSilverbackAdmin", principalId),
    buildLegacyRightRequest("paRightIdSilverbackSuperAdmin", principalId),
  ];
}

export async function canViewSettings(routeContext: PaxContext): Promise<boolean> {
  const { auth, institutionId } = routeContext;
  const results = await hasPermissions(
    buildCanViewSettingsRequests(auth.principalId, institutionId),
    routeContext
  );
  return results.some(Boolean);
}

export function useCanViewSettingsQuery() {
  const { principalId } = useAuth();
  const { institutionId } = useRouteContext({ from: "__root__" });

  const query = useHasPermissionsQuery(buildCanViewSettingsRequests(principalId, institutionId!));

  return {
    ...query,
    data: query.data ? query.data.some(Boolean) : undefined,
  };
}

export function useCanViewTermsAndConditionsQuery() {
  const { principalId } = useAuth();

  const silverbackAdmin = useHasPermissionQuery(
    buildLegacyRightRequest("paRightIdSilverbackAdmin", principalId)
  );
  const silverbackSuperAdmin = useHasPermissionQuery(
    buildLegacyRightRequest("paRightIdSilverbackSuperAdmin", principalId)
  );

  return {
    isLoading: silverbackAdmin.isLoading || silverbackSuperAdmin.isLoading,
    data:
      silverbackAdmin.data === undefined && silverbackSuperAdmin.data === undefined
        ? undefined
        : Boolean(silverbackAdmin.data) || Boolean(silverbackSuperAdmin.data),
  };
}

function buildCanCreateIOIRequest(principalId: string, institutionId: number): PermissionRequest {
  return {
    principalId,
    entityType: "PAX::MKT_INSTITUTION",
    entityId: `mkt-inst:${institutionId}`,
    actionId: "createIOI",
  };
}

export function useCanCreateIOIQuery() {
  const { principalId } = useAuth();
  const { institutionId } = useRouteContext({ from: "__root__" });

  return useHasPermissionQuery(buildCanCreateIOIRequest(principalId, institutionId!));
}

function buildCanPublishListingRequest(
  principalId: string,
  institutionId: number
): PermissionRequest {
  return {
    principalId,
    entityType: "PAX::MKT_INSTITUTION",
    entityId: `mkt-inst:${institutionId}`,
    actionId: "list",
  };
}

export function useCanPublishListingQuery() {
  const { principalId } = useAuth();
  const { institutionId } = useRouteContext({ from: "__root__" });

  return useHasPermissionQuery(buildCanPublishListingRequest(principalId, institutionId!));
}

/* -------------------------------------------------------------------------- */
/* Initial site-access gate (runs at the root)                                */
/* -------------------------------------------------------------------------- */

export const getInitialPermissions = async (routeContext: PaxContext): Promise<void> => {
  const { auth, institutionId } = routeContext;

  const institutionMemberRequest: PermissionRequest = {
    principalId: auth.principalId,
    entityType: "PAX::MKT_INSTITUTION",
    entityId: `mkt-inst:${institutionId}`,
    actionId: "isInstitutionMember",
  };
  const nCinoEmployeeRequest = buildLegacyRightRequest("nCinoEmployee", auth.principalId);
  const silverbackAdminRequest = buildLegacyRightRequest(
    "paRightIdSilverbackAdmin",
    auth.principalId
  );
  const silverbackSuperAdminRequest = buildLegacyRightRequest(
    "paRightIdSilverbackSuperAdmin",
    auth.principalId
  );
  const viewAccessAdminRequest = buildPermissionGroupRequest(
    "viewAccessAdmin",
    auth.principalId,
    institutionId
  );
  const canCreateIOIRequest = buildCanCreateIOIRequest(auth.principalId, institutionId);
  const canPublishListing = buildCanPublishListingRequest(auth.principalId, institutionId);

  // One batched request; each result (including silverbackAdminRequest, silverbackSuperAdminRequest, viewAccessAdmin, etc.) is seeded into its own cache.
  const [isInstitutionMember, isnCinoEmployee] = await hasPermissions(
    [
      institutionMemberRequest,
      nCinoEmployeeRequest,
      silverbackAdminRequest,
      silverbackSuperAdminRequest,
      viewAccessAdminRequest,
      canCreateIOIRequest,
      canPublishListing,
    ],
    routeContext
  );

  if (!isInstitutionMember && !isnCinoEmployee) {
    throw new MarketplaceAccessDeniedError();
  }
};
